import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { env } from '../../../env';

/**
 * A public API client that does not require authentication.
 * Use this for endpoints that are publicly accessible without a user session.
 */
class PublicApiClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({ baseURL });
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        console.log('Public API error:', error);
        return Promise.reject(error);
      },
    );
  }

  /**
   * Sends a GET request.
   * @param url The URL to request.
   * @param config Optional Axios request configuration.
   * @returns A Promise that resolves with the response data.
   */
  public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance
      .get(url, config)
      .then((response) => response.data);
  }

  /**
   * Sends a POST request.
   * @param url The URL to request.
   * @param data The data to send in the request body.
   * @param config Optional Axios request configuration.
   * @returns A Promise that resolves with the response data.
   */
  public post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.axiosInstance
      .post(url, data, config)
      .then((response) => response.data);
  }

  /**
   * Sends a PUT request.
   * @param url The URL to request.
   * @param data The data to send in the request body.
   * @param config Optional Axios request configuration.
   * @returns A Promise that resolves with the response data.
   */
  public put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.axiosInstance
      .put(url, data, config)
      .then((response) => response.data);
  }

  /**
   * Sends a PATCH request.
   * @param url The URL to request.
   * @param data The data to send in the request body.
   * @param config Optional Axios request configuration.
   * @returns A Promise that resolves with the response data.
   */
  public patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.axiosInstance
      .patch(url, data, config)
      .then((response) => response.data);
  }

  /**
   * Sends a DELETE request.
   * @param url The URL to request.
   * @param config Optional Axios request configuration.
   * @returns A Promise that resolves with the response data.
   */
  public delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance
      .delete(url, config)
      .then((response) => response.data);
  }
}

export const publicApiClient = new PublicApiClient(env.VITE_BASE_API_URL ?? '');

export default PublicApiClient;
